import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import {
	NewBookingsStore,
	RootStore,
	AddressesStore,
	PassengerDetailsStore,
} from 'AppClient/stores';
import { ListAlert } from 'components';
import { Form, Alert, TimePicker, Collapse } from 'components/antd';
import LocationsDnD from './LocationsDnD';
import CreditCardsSection from './CreditCardsSection';

import ChooseVehicleType from 'AppClient/components/ChooseVehicleType';
import DatePicker from 'AppClient/components/DatePicker';
import moment, { Moment } from 'moment';
import { isEmpty } from 'lodash-es';
import AmendPassengerDetailsForm from 'AppClient/components/AmendPassengerDetails/AmendPassengerDetailsForm';

import { DATE_FORMAT, TIME_FORMAT, DATE_TIME_FORMAT } from 'app-constants';

import styles from './styles.module.scss';

export const CONTACT_DETAILS = 'contactDetails';

interface Props {
	newBookingsStore?: NewBookingsStore;
	addressesStore?: AddressesStore;
	passengerDetailsStore?: PassengerDetailsStore;
}

@inject(({ newBookingsStore, addressesStore, passengerDetailsStore }: RootStore) => ({
	newBookingsStore,
	addressesStore,
	passengerDetailsStore,
}))
@observer
export class PricingBooking extends Component<Props> {
	onDateHandler = (date: Moment) => {
		const { pickupDateTime } = this.props.newBookingsStore!;
		const { resetSuggestions } = this.props.addressesStore!;
		const pickupDate = date === null ? '' : date.format(DATE_FORMAT);
		const pickupTime = pickupDateTime ? pickupDateTime.format(TIME_FORMAT) : '';

		// need to reset suggestions in order to have search again for flights
		resetSuggestions();
		this.updateDateTime(pickupDate, pickupTime);
	};

	onTimeHandler = (time: Moment) => {
		const { pickupDateTime } = this.props.newBookingsStore!;
		const pickupTime = time === null ? '' : time.format(TIME_FORMAT);
		const pickupDate = pickupDateTime ? pickupDateTime.format(DATE_FORMAT) : '';

		this.updateDateTime(pickupDate, pickupTime);
	};

	updateDateTime = (pickupDate: string, pickupTime: string) => {
		const { isRebooking } = this.props.newBookingsStore!;

		const now = moment();
		let date;

		if (pickupDate && pickupTime) {
			date = `${pickupDate} ${pickupTime}`;
		} else if (pickupDate && !pickupTime && isRebooking) {
			date = `${pickupDate} ${now.format(TIME_FORMAT)}`;
		} else if (pickupDate) {
			date = `${pickupDate}`;
		} else if (pickupTime) {
			date = `${now.format(DATE_FORMAT)} ${pickupTime}`;
		} else {
			date = now.format(DATE_TIME_FORMAT);
		}

		this.props.newBookingsStore!.updatePricesPayload({
			pickupDateTimeInLocationTimeZone: moment(date, DATE_TIME_FORMAT).format(
				DATE_TIME_FORMAT
			),
		});
	};

	getAlertsInfo() {
		const alerts = [];
		const { validationAddressErrors, addresses } = this.props.addressesStore!;
		for (const [errorKey, errorItem] of Object.entries(validationAddressErrors)) {
			let currentIndex;
			let index = 0;
			Object.entries(addresses).forEach(([addrKey]) => {
				if (addrKey === errorKey) {
					currentIndex = index;
				}
				index++;
			});
			let listHeader = 'Going through location:';
			if (currentIndex === 0) {
				listHeader = 'Pickup location:';
			}
			if (currentIndex === Object.keys(addresses).length - 1) {
				listHeader = 'Dropoff location:';
			}
			if (!isEmpty(errorItem)) {
				alerts.push({
					messages: errorItem,
					listHeader,
				});
			}
		}
		return alerts.reverse();
	}

	render() {
		const { pickupDateTime } = this.props.newBookingsStore!;
		const { isFlightSelectedInFirstLocation } = this.props.addressesStore!;
		const {
			isAmendingDetailsFormValid,
			getOpenDetailsKey,
			changeOpenDetailsKey,
			isFieldsTouched,
		} = this.props.passengerDetailsStore!;
		const alerts = this.getAlertsInfo();
		const collapseProps = {};

		if (!isFieldsTouched) {
			collapseProps['activeKey'] = getOpenDetailsKey;
			collapseProps['onChange'] = changeOpenDetailsKey;
		}

		return (
			<div className={styles.bookingPricing}>
				<h2>trip details</h2>
				<div className="modalBoxWrapper">
					<div className="modalBox modalBoxBooking modalBoxNoPb">
						<div className="modalBoxRow bookingPricingDateRow">
							<div className="modalBoxCol">
								<Form.Item label="PICKUP DATE">
									<DatePicker
										value={pickupDateTime}
										onChange={this.onDateHandler}
										style={{
											minWidth: '114px',
										}}
									/>
								</Form.Item>
							</div>
							<div className="modalBoxCol" id="bookingTimePicker">
								<Form.Item
									label="PICKUP TIME"
									className={isFlightSelectedInFirstLocation ? 'disabled' : ''}
								>
									<TimePicker
										value={pickupDateTime}
										format={TIME_FORMAT}
										onChange={this.onTimeHandler}
										disabled={isFlightSelectedInFirstLocation}
										className={styles.timePickerCustomer}
										placeholder="hh:mm"
										getPopupContainer={() =>
											document.getElementById('bookingTimePicker') ||
											document.body
										}
										popupClassName={styles.timePickerPopupCustomer}
									/>
								</Form.Item>
							</div>
							{isFlightSelectedInFirstLocation && (
								<div className={styles.timePickerInfo}>
									*Adjusted using flight tracking.
									<br /> To delay pick-up, please add notes to driver
								</div>
							)}
						</div>
						<LocationsDnD />
						<CreditCardsSection />
						<ChooseVehicleType />
					</div>
					<Collapse {...collapseProps}>
						<Collapse.Panel
							header={'Contact details'}
							key={CONTACT_DETAILS}
							disabled={!isAmendingDetailsFormValid}
						>
							<AmendPassengerDetailsForm />
						</Collapse.Panel>
					</Collapse>
				</div>
				{alerts.length > 0 && (
					<div className={styles.bookingAlerts}>
						{alerts.map((item, index) => {
							const message = (
								<ListAlert errors={item.messages} listHeader={item.listHeader} />
							);
							return (
								<Alert
									type="error"
									message={message}
									className={styles[`alertNumber-${index}`]}
									showIcon
									key={`${item.listHeader}-${index}`}
								/>
							);
						})}
					</div>
				)}
			</div>
		);
	}
}
